//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { type JobPhase, type JobProgress } from '@/services/apiTypes';
import { formatInt } from '@/utils/Format';

// Phase order per job kind, for the "step N of M" indicator.  Loads ingest
// (reading -> building -> committing); queries run then commit.
export const LOAD_PHASES: JobPhase[] = ['reading', 'building', 'committing'];
const QUERY_PHASES: JobPhase[] = ['executing', 'committing'];

const PHASE_LABELS: Record<string, string> = {
  reading: 'Reading',
  building: 'Building',
  executing: 'Executing',
  committing: 'Committing',
  writing: 'Writing',
  done: 'Done'
};

export function phaseLabel(phase: string): string {
  return PHASE_LABELS[phase] ??
    (phase ? phase.charAt(0).toUpperCase() + phase.slice(1) : '');
}

// `~62%` (estimate) or `62%`, or '' when there's no percent to show.
export function percentLabel(p: JobProgress): string {
  if (p.percent == null) return '';
  return `${p.percent_is_estimate ? '~' : ''}${Math.round(p.percent)}%`;
}

// Position of the current phase within its job's sequence, e.g.
// { step: 1, total: 3 }; null for done/unspecified.  Loads vs queries are told
// apart by phase, with rows_processed > 0 disambiguating the shared
// `committing` phase (a load streamed rows, a query didn't).
export function phaseStep(
  p: JobProgress
): { step: number; total: number } | null {
  const isLoad = p.phase === 'reading' || p.phase === 'building' ||
    p.rows_processed > 0;
  const seq = isLoad ? LOAD_PHASES : QUERY_PHASES;
  const idx = seq.indexOf(p.phase);
  return idx < 0 ? null : { step: idx + 1, total: seq.length };
}

// Whether the live telemetry block has anything to show: a bar while the job
// executes, or a throughput row once edges accrue.  Lets a caller gate an
// adjacent divider so it doesn't leave a stray line when there's no telemetry.
export function hasLiveStats(
  running: boolean,
  progress?: JobProgress | null,
  visitedEdges?: Record<string, number> | null
): boolean {
  const visTotal = visitedEdges ?
    Object.values(visitedEdges).reduce((a, b) => a + b, 0) : 0;
  const total = visTotal || progress?.traversed_edges || 0;
  return running || total > 0;
}

// Abbreviated count for the narrow status cell: 1234567 -> "1.2M".
export function compactCount(n: number): string {
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return String(n);
}

// Live query activity, e.g. "4.2M edges, 1.1M/s".  Formatted client-side (the
// engine sends the raw count + phase elapsed).  '' when nothing traversed yet.
export function formatEdges(p: JobProgress): string {
  if (p.traversed_edges <= 0) return '';
  const edges = `${compactCount(p.traversed_edges)} edges`;
  if (p.phase_elapsed_ms > 0) {
    const perSec = Math.round(p.traversed_edges / (p.phase_elapsed_ms / 1000));
    return `${edges}, ${compactCount(perSec)}/s`;
  }
  return edges;
}

// Split the live status into a truncatable label (phase + step) and a detail
// (percent / row count / substep).  The status cell pins the detail and lets
// only the label ellipsize, so the percent is never the part that gets cut.
// `detail` is '' when there's nothing within-phase to show.
export function formatProgressParts(
  p: JobProgress
): { label: string; detail: string } {
  const pct = percentLabel(p);
  // A substep is the most specific detail once active progress can't advance
  // it further: when there's no percent at all (a query's live edge count) or
  // the percent has maxed at 100 (a load building, a save finalizing).  Shown
  // alone so the cell isn't cluttered with both the phase step and a "100%".
  const preferSubstep = !!p.substep &&
    (p.percent == null || p.percent >= 100);
  let detail = '';
  let showStep = true;
  if (preferSubstep) {
    detail = p.substep ?? '';
    showStep = false;
  } else if (pct) {
    detail = pct;
  } else if (p.rows_processed > 0) {
    detail = compactCount(p.rows_processed);
  } else if (p.traversed_edges > 0) {
    // A running query: live edges traversed + rate, shown alone (no phase step).
    detail = formatEdges(p);
    showStep = false;
  }
  const step = showStep ? phaseStep(p) : null;
  const stepLabel = step ? ` ${step.step}/${step.total}` : '';
  return { label: `${phaseLabel(p.phase)}${stepLabel}`, detail };
}

// Wall-clock time spent in the current phase, e.g. "12s" or "1m 05s".
export function formatPhaseElapsed(ms: number): string {
  const total = Math.floor(ms / 1000);
  if (total < 60) return `${total}s`;
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}m ${String(s).padStart(2, '0')}s`;
}

// Fuller one-line detail for a hover tooltip: phase · substep · N / M rows ·
// elapsed.
export function formatProgressTooltip(p: JobProgress): string {
  const rows = p.rows_processed > 0 ?
    `${formatInt(p.rows_processed)}` +
    `${p.rows_total != null ? ` / ${formatInt(p.rows_total)}` : ''} rows` : '';
  const step = phaseStep(p);
  const stepLabel = step ? `step ${step.step} of ${step.total}` : '';
  const parts = [phaseLabel(p.phase), stepLabel, p.substep ?? '',
    formatEdges(p), rows,
    formatPhaseElapsed(p.phase_elapsed_ms)].filter(Boolean);
  return parts.join(' · ');
}
